/**
 * rp-documento — Parses raw text downloaded from Panama's Registro Público
 * portal (pasted by the user into the Cumpli copilot).
 *
 * Three input shapes, auto-detected:
 *  - "extra_datos":     "[TAB: <Sección>]" blocks (Prelación, Registro Previo
 *                       Activo/Inactivo, Registro Electrónico, Miembros…).
 *                       Entries inside a section are separated by dash lines
 *                       and hold "Clave: valor" pairs.
 *  - "datos_generales": the portal's folio-export page text, e.g.
 *                       "(INMUEBLE) DAVID Código de Ubicación 45, Folio Real Nº 9 (F)".
 *  - "texto_libre":     anything else (OCR of certificados/escrituras) —
 *                       only the generic extractors run.
 *
 * The regex catalogue is a faithful port of Cogno's pdf_extractor.py.
 * Permissive bias: a field that cannot be parsed cleanly is simply absent —
 * never guessed. User-facing strings (alertas/advertencias) are es-PA.
 */
const MAX_CARACTERES = 60000;
// ---------------------------------------------------------------------------
// Normalization helpers
// ---------------------------------------------------------------------------
/** Strips diacritics. Length-preserving for precomposed input, so indexes
 * into the folded string remain valid in the original. */
function plegarAcentos(s) {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
function plegarMayus(s) {
    return plegarAcentos(s).toUpperCase();
}
/** Normalized lookup key: accent-free, lowercase, º/° → o, collapsed spaces. */
function plegarClave(s) {
    return plegarAcentos(s)
        .toLowerCase()
        .replace(/[º°]/g, 'o')
        .replace(/\s+/g, ' ')
        .trim();
}
function unicos(valores) {
    return [...new Set(valores.filter((v) => v.length > 0))];
}
// ---------------------------------------------------------------------------
// Regex catalogue (ported from Cogno's pdf_extractor.py)
// ---------------------------------------------------------------------------
// Transaction-type keywords, longest first so "COMPRAVENTA" is not caught by
// "VENTA" nor "CANCELACION DE HIPOTECA" by "HIPOTECA". Compared against the
// accent-folded uppercase paragraph, so accented variants collapse.
const TIPOS_TRANSACCION = [
    ['CANCELACION DE HIPOTECA', 'CANCELACION_HIPOTECA'],
    ['RECTIFICACION', 'RECTIFICACION'],
    ['ADJUDICACION', 'ADJUDICACION'],
    ['COMPRA-VENTA', 'COMPRA_VENTA'],
    ['COMPRAVENTA', 'COMPRA_VENTA'],
    ['SEGREGACION', 'SEGREGACION'],
    ['FIDEICOMISO', 'FIDEICOMISO'],
    ['USUFRUCTO', 'USUFRUCTO'],
    ['HIPOTECA', 'HIPOTECA'],
    ['DONACION', 'DONACION'],
    ['PERMUTA', 'PERMUTA'],
    ['EMBARGO', 'EMBARGO'],
    ['CESION', 'CESION'],
    ['VENTA', 'VENTA'],
];
const MESES_ES = {
    enero: 1,
    febrero: 2,
    marzo: 3,
    abril: 4,
    mayo: 5,
    junio: 6,
    julio: 7,
    agosto: 8,
    septiembre: 9,
    setiembre: 9,
    octubre: 10,
    noviembre: 11,
    diciembre: 12,
};
const FECHA_NUMERICA_RE = /\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/;
const FECHA_ESCRITA_RE = /\b(\d{1,2})\s+de\s+([A-Za-zñÑáéíóúÁÉÍÓÚ]+)\s+de\s+(\d{4})\b/i;
const MONTO_RE = /(?:B\/?\.?|USD|US\$|\$)\s*(\d{1,3}(?:[,.\s]\d{3})*(?:[,.]\d{1,2})?)\b/i;
const REFERENCIA_FOLIO_RE_G = /\b(?:finca|folio|tomo|asiento)\s*(?:no\.?|n[uú]mero)?\s*(\d{3,7})\b/gi;
// Owner labels (rol es → canonical role, as in the Python source).
const ETIQUETAS_PARTES = [
    ['propietario', 'OWNER'],
    ['dueño', 'OWNER'],
    ['dueno', 'OWNER'],
    ['vendedor', 'SELLER'],
    ['comprador', 'BUYER'],
    ['hipotecante', 'MORTGAGOR'],
    ['acreedor hipotecario', 'MORTGAGEE'],
    ['cedente', 'ASSIGNOR'],
    ['cesionario', 'ASSIGNEE'],
    ['donante', 'DONOR'],
    ['donatario', 'DONEE'],
    ['apoderado', 'ATTORNEY_IN_FACT'],
    ['representante legal', 'LEGAL_REPRESENTATIVE'],
    ['director', 'DIRECTOR'],
    ['dignatario', 'OFFICER'],
    ['presidente', 'PRESIDENT'],
    ['secretario', 'SECRETARY'],
    ['tesorero', 'TREASURER'],
];
const ROL_POR_ETIQUETA = new Map(ETIQUETAS_PARTES.map(([etiqueta, rol]) => [plegarClave(etiqueta), rol]));
const DIGNATARIO_TITULOS = new Map([
    ['presidente', 'Presidente'],
    ['vicepresidente', 'Vicepresidente'],
    ['secretario', 'Secretario'],
    ['subsecretario', 'Subsecretario'],
    ['tesorero', 'Tesorero'],
    ['subtesorero', 'Subtesorero'],
    ['vocal', 'Vocal'],
]);
// La coincidencia abajo es por substring (`includes`), así que un título más corto
// que es prefijo/substring de otro (p. ej. 'presidente' dentro de 'vicepresidente')
// puede ganar por error si se itera en el orden de inserción. Para que el resultado
// no dependa del orden del literal de arriba, se ordena esta vista por longitud de
// clave DESCENDENTE una sola vez a nivel de módulo, y es la que se recorre en
// mapearCargoRegistro — así el título más específico (más largo) siempre gana.
const DIGNATARIO_TITULOS_POR_LONGITUD = [
    ...DIGNATARIO_TITULOS,
].sort((a, b) => b[0].length - a[0].length);
/** Maps a raw RP `cargo` string to the Cumpli rol + specific dignatario posición.
 *  Dignatario titles (presidente/secretario/tesorero…) → rol='dignatario' + posición. */
export function mapearCargoRegistro(cargoCrudo) {
    const clave = plegarClave(cargoCrudo ?? '');
    for (const [titulo, etiqueta] of DIGNATARIO_TITULOS_POR_LONGITUD) {
        if (clave.includes(titulo))
            return { rol: 'dignatario', posicion: etiqueta };
    }
    if (clave.includes('director'))
        return { rol: 'director', posicion: null };
    if (clave.includes('dignatario'))
        return { rol: 'dignatario', posicion: null };
    if (clave.includes('apoderado'))
        return { rol: 'apoderado', posicion: null };
    return { rol: null, posicion: null };
}
// Horizontal-only whitespace after the label/colon (deliberate deviation from the
// Python reference, whose \s* crosses newlines): the portal's export puts each
// label on its own line ("PROPIETARIO:\nDOMICILIO:"), and a newline-crossing
// match fabricates a parte named like the next label. A value must share the line.
const LINEA_PARTE_RE_G = new RegExp('\\b(' +
    ETIQUETAS_PARTES.map(([etiqueta]) => etiqueta).join('|') +
    ')[^\\S\\r\\n]*[:\\-][^\\S\\r\\n]*([^\\n\\r]{3,200})', 'gi');
// Cut-off: a name ends at con/que/RUC/C.I.P./". "/";" (as in _extract_owners).
const CORTE_NOMBRE_RE = /\bcon\b|\bque\b|\bRUC\b|\bC\.I\.P\.|\.\s|;/i;
const CEDULA_RE = /\b(?:c[eé]dula|c\.?i\.?p\.?|cip)\s*(?:n[oº.]?\s*)?(\d{1,2}[-\s]?\d{1,4}[-\s]?\d{1,6})\b/i;
const CEDULA_RE_G = new RegExp(CEDULA_RE.source, 'gi');
const RUC_RE = /\bRUC\s*(?:n[oº.]?\s*)?([0-9-]+(?:\s*DV\s*\d+)?)\b/i;
const RUC_RE_G = new RegExp(RUC_RE.source, 'gi');
// extra_datos structure
const TAB_RE_G = /\[TAB:\s*([^\]]+)\]/gi;
const SEPARADOR_ENTRADAS_RE = /^\s*-{4,}\s*$/m;
const SOLO_PUNTUACION_RE = /^[\s.,;:\-_'"´`]*$/;
// Asiento lines
const ASIENTO_ID_RE = /Asiento\s+Id\s+-(\d+)-/i;
const ASIENTO_ACTO_RE = /Asiento\s+Id\s+-\d+-\s*\(([^)]*)\)/i;
const ASIENTO_ENTRADA_RE = /\bEntrada\s+(\d+\/\d+)/i;
const ASIENTO_FAVOR_RE = /(?:Propiedad|Hipoteca)\s+a\s+favor\s+de\s+(.+?)(?=\s+C[eé]dula\b|\s+Asiento\s+Id\b|\s*$)/i;
// datos_generales
const LINEA_FOLIO_RE = /\(([A-ZÑÁÉÍÓÚ][A-ZÑÁÉÍÓÚ\s]*)\)\s*([^\n]*?)\s*C[oó]digo\s+de\s+Ubicaci[oó]n\s+(\d+)\s*,?\s*Folio\s+Real\s+N[ºo°.]*\s*(\d+)/i;
const PROPIETARIO_RE = /([^\n()]{3,150}?)\s*\(\s*Propiedad\s*\)/i;
const SECCION_POR_TIPO = {
    INMUEBLE: 'Inmuebles',
    SOCIEDAD: 'Mercantil',
    MERCANTIL: 'Mercantil',
    PERSONA: 'Personas',
    'BIEN MUEBLE': 'Bienes Muebles',
    MUEBLE: 'Bienes Muebles',
};
// ---------------------------------------------------------------------------
// Field parsers
// ---------------------------------------------------------------------------
/** dd/mm/yyyy (two-digit year: >=30 → 19xx, else 20xx) or "5 de mayo de 1987"
 * → ISO YYYY-MM-DD, validating day 1-31 / month 1-12 / year 1900-2100. */
function parseFecha(s) {
    const mNum = FECHA_NUMERICA_RE.exec(s);
    if (mNum) {
        const dia = Number(mNum[1]);
        const mes = Number(mNum[2]);
        let anio = Number(mNum[3]);
        if (anio < 100)
            anio = anio >= 30 ? 1900 + anio : 2000 + anio;
        if (dia >= 1 && dia <= 31 && mes >= 1 && mes <= 12 && anio >= 1900 && anio <= 2100) {
            return `${String(anio).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        }
    }
    const mTxt = FECHA_ESCRITA_RE.exec(s);
    if (mTxt) {
        const dia = Number(mTxt[1]);
        const mes = MESES_ES[plegarClave(mTxt[2] ?? '')];
        const anio = Number(mTxt[3]);
        if (mes !== undefined && dia >= 1 && dia <= 31 && anio >= 1900 && anio <= 2100) {
            return `${String(anio).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        }
    }
    return undefined;
}
/** "B/. 93,054.00" → 93054 PAB; "US$ 1.500.000,50" → 1500000.5 USD.
 * When both separators appear, the rightmost one is the decimal separator;
 * a lone comma group with 2 decimals is a decimal, otherwise thousands. */
function parseMonto(s) {
    const m = MONTO_RE.exec(s);
    const crudo = m?.[1];
    if (m === null || crudo === undefined)
        return undefined;
    let limpio = crudo.replace(/\s/g, '');
    const ultimaComa = limpio.lastIndexOf(',');
    const ultimoPunto = limpio.lastIndexOf('.');
    if (ultimaComa >= 0 && ultimoPunto >= 0) {
        limpio =
            ultimaComa > ultimoPunto
                ? limpio.replace(/\./g, '').replace(/,/g, '.')
                : limpio.replace(/,/g, '');
    }
    else if (ultimaComa >= 0) {
        const partes = limpio.split(',');
        limpio =
            (partes[partes.length - 1] ?? '').length === 2
                ? limpio.replace(/,/g, '.')
                : limpio.replace(/,/g, '');
    }
    const monto = Number(limpio);
    if (!Number.isFinite(monto))
        return undefined;
    const bruto = (m[0] ?? '').toUpperCase();
    const moneda = bruto.includes('USD') || bruto.includes('US$') || bruto.startsWith('$') ? 'USD' : 'PAB';
    return { monto, moneda };
}
// ---------------------------------------------------------------------------
// Generic extractors (run on every format)
// ---------------------------------------------------------------------------
function extraerPartes(texto) {
    const partes = [];
    for (const m of texto.matchAll(LINEA_PARTE_RE_G)) {
        const etiqueta = plegarClave(m[1] ?? '');
        let nombre = (m[2] ?? '').trim();
        nombre = nombre.split(CORTE_NOMBRE_RE)[0] ?? nombre;
        nombre = nombre.replace(/^[\s.,;:-]+/, '').replace(/[\s.,;:-]+$/, '');
        if (nombre.length < 3 || nombre.length > 200)
            continue;
        const parte = { rol: ROL_POR_ETIQUETA.get(etiqueta) ?? 'UNKNOWN', nombre };
        const ventana = texto.slice(m.index ?? 0, (m.index ?? 0) + 400);
        const cedula = CEDULA_RE.exec(ventana)?.[1]?.trim();
        if (cedula !== undefined && cedula.length > 0)
            parte.cedula = cedula;
        const ruc = RUC_RE.exec(ventana)?.[1]?.trim();
        if (ruc !== undefined && ruc.length > 0)
            parte.ruc = ruc;
        partes.push(parte);
    }
    return partes;
}
/** One transaction per double-newline paragraph; first keyword wins.
 * Date and amount come from the same paragraph. */
function extraerTransacciones(texto) {
    const transacciones = [];
    for (const parrafo of texto.split(/\n\s*\n/)) {
        if (parrafo.length < 20)
            continue;
        const mayus = plegarMayus(parrafo);
        for (const [palabra, canonico] of TIPOS_TRANSACCION) {
            if (!mayus.includes(palabra))
                continue;
            const tx = { tipo: canonico };
            const fecha = parseFecha(parrafo);
            if (fecha !== undefined)
                tx.fecha = fecha;
            const monto = parseMonto(parrafo);
            if (monto !== undefined) {
                tx.monto = monto.monto;
                tx.moneda = monto.moneda;
            }
            transacciones.push(tx);
            break;
        }
    }
    return transacciones;
}
function paresDeEntrada(entrada) {
    const pares = [];
    for (const linea of entrada.split(/\r?\n/)) {
        const m = /^([^:]+):\s*(.*)$/.exec(linea);
        if (m)
            pares.push([plegarClave(m[1] ?? ''), (m[2] ?? '').trim()]);
    }
    return pares;
}
function valorDe(pares, clave) {
    const valor = pares.find(([k]) => k === clave)?.[1];
    return valor !== undefined && valor.length > 0 ? valor : undefined;
}
function construirAsiento(descripcion, fecha) {
    const asiento = { descripcion };
    const asientoId = ASIENTO_ID_RE.exec(descripcion)?.[1];
    if (asientoId !== undefined)
        asiento.asientoId = asientoId;
    const acto = ASIENTO_ACTO_RE.exec(descripcion)?.[1]?.trim();
    if (acto !== undefined && acto.length > 0)
        asiento.acto = acto;
    const entrada = ASIENTO_ENTRADA_RE.exec(descripcion)?.[1];
    if (entrada !== undefined)
        asiento.entrada = entrada;
    const favorDe = ASIENTO_FAVOR_RE.exec(descripcion)?.[1]?.trim();
    if (favorDe !== undefined && favorDe.length > 0)
        asiento.favorDe = favorDe;
    const cedula = CEDULA_RE.exec(descripcion)?.[1]?.trim();
    if (cedula !== undefined && cedula.length > 0)
        asiento.cedula = cedula;
    if (fecha !== undefined)
        asiento.fecha = fecha;
    return asiento;
}
function parseExtraDatos(cuerpo, r) {
    const encabezados = [...cuerpo.matchAll(TAB_RE_G)];
    const desconocidas = new Set();
    for (let i = 0; i < encabezados.length; i++) {
        const encabezado = encabezados[i];
        if (encabezado === undefined)
            continue;
        const nombre = (encabezado[1] ?? '').trim();
        const clave = plegarClave(nombre);
        const inicio = (encabezado.index ?? 0) + (encabezado[0] ?? '').length;
        const fin = encabezados[i + 1]?.index ?? cuerpo.length;
        const entradas = cuerpo
            .slice(inicio, fin)
            .split(SEPARADOR_ENTRADAS_RE)
            .map((e) => e.trim())
            .filter((e) => e.length > 0);
        if (clave === 'miembros relacionados') {
            for (const entrada of entradas) {
                const pares = paresDeEntrada(entrada);
                const cargo = valorDe(pares, 'cargo');
                const nombreMiembro = pares.find(([k]) => k === 'miembro')?.[1];
                if (cargo === undefined || nombreMiembro === undefined) {
                    r.advertencias.push('Se omitió una entrada de miembros sin cargo o sin nombre.');
                    continue;
                }
                if (SOLO_PUNTUACION_RE.test(nombreMiembro)) {
                    r.advertencias.push(`Se omitió el miembro con cargo "${cargo}" por no tener un nombre válido.`);
                    continue;
                }
                r.miembros.push({ cargo, nombre: nombreMiembro });
            }
        }
        else if (clave === 'prelacion') {
            for (const entrada of entradas) {
                const pares = paresDeEntrada(entrada);
                const numero = valorDe(pares, 'no de entrada');
                if (numero === undefined) {
                    r.advertencias.push('Se omitió una entrada de prelación sin número de entrada.');
                    continue;
                }
                const item = { entrada: numero };
                const tipo = valorDe(pares, 'tipo de entrada');
                if (tipo !== undefined)
                    item.tipo = tipo;
                const tramite = valorDe(pares, 'tramite');
                if (tramite !== undefined)
                    item.tramite = tramite;
                const estado = valorDe(pares, 'estado');
                if (estado !== undefined)
                    item.estado = estado;
                r.prelacion.push(item);
            }
        }
        else if (clave === 'registro previo activo (emulador)' ||
            clave === 'registro electronico vigentes' ||
            clave === 'registro previo inactivo (emulador)' ||
            clave === 'registro electronico no vigentes') {
            const vigente = clave === 'registro previo activo (emulador)' || clave === 'registro electronico vigentes';
            const destino = vigente ? r.asientosVigentes : r.asientosNoVigentes;
            for (const entrada of entradas) {
                const pares = paresDeEntrada(entrada);
                const descripcion = valorDe(pares, 'derechos / actos / otras operaciones');
                if (descripcion === undefined) {
                    r.advertencias.push(`Se omitió un asiento sin descripción en "[TAB: ${nombre}]".`);
                    continue;
                }
                destino.push(construirAsiento(descripcion, parseFecha(valorDe(pares, 'fecha') ?? '')));
            }
        }
        else if (clave === 'folios madre' || clave === 'folios resultantes') {
            // Informational lineage only — the folio referenced there is the parent /
            // resulting folio, not this record's; never promoted to `folio`.
        }
        else {
            desconocidas.add(nombre);
        }
    }
    for (const nombre of desconocidas) {
        r.advertencias.push(`Sección no reconocida: "[TAB: ${nombre}]"; su contenido no se interpretó.`);
    }
}
// ---------------------------------------------------------------------------
// datos_generales parsing
// ---------------------------------------------------------------------------
function parseDatosGenerales(cuerpo, buscable, r) {
    const m = LINEA_FOLIO_RE.exec(cuerpo);
    if (m) {
        const tipo = plegarMayus((m[1] ?? '').trim()).replace(/\s+/g, ' ');
        const seccion = SECCION_POR_TIPO[tipo];
        if (seccion !== undefined)
            r.seccion = seccion;
        const municipio = (m[2] ?? '').trim();
        if (municipio.length > 0)
            r.municipio = municipio;
        const codigo = m[3];
        if (codigo !== undefined)
            r.codigoUbicacion = codigo;
        const folio = m[4];
        if (folio !== undefined)
            r.folio = folio;
    }
    const propietario = PROPIETARIO_RE.exec(cuerpo)?.[1]?.trim();
    if (propietario !== undefined && propietario.length >= 3)
        r.denominacion = propietario;
    // OCR often separates labels from values, so instead of label adjacency we
    // take the first *valid* date at/after the label (or the DATOS DEL FOLIO block).
    let fecha;
    const idxEtiqueta = buscable.indexOf('fecha de inscripcion');
    if (idxEtiqueta >= 0)
        fecha = parseFecha(cuerpo.slice(idxEtiqueta));
    if (fecha === undefined) {
        const idxBloque = buscable.indexOf('datos del folio');
        if (idxBloque >= 0)
            fecha = parseFecha(cuerpo.slice(idxBloque));
    }
    if (fecha !== undefined)
        r.fechaInscripcion = fecha;
}
// ---------------------------------------------------------------------------
// Estado, gravámenes, alertas
// ---------------------------------------------------------------------------
const ESTADOS_SOCIEDAD = new Set(['VIGENTE', 'SUSPENDIDO', 'DISUELTO', 'FUSION']);
// A prelación trámite can itself carry "Estado: Suspendido" (real corpus:
// fundaciones VIGENTES with trámites suspendidos), which collides with the
// society-status words — so the explicit scan must never look inside
// [TAB: Prelación] blocks.
const BLOQUE_PRELACION_RE_G = /\[TAB:\s*Prelaci[oó]n\s*\][\s\S]*?(?=\[TAB:|$)/gi;
function detectarEstado(cuerpo, r) {
    // Explicit "Estado:" line — only when the value is a society status, and only
    // outside prelación (whose trámite Estado lines are filing states, not the
    // society's status).
    const cuerpoSinPrelacion = cuerpo.replace(BLOQUE_PRELACION_RE_G, '');
    for (const m of cuerpoSinPrelacion.matchAll(/^[ \t]*Estado\s*:\s*(.+)$/gim)) {
        const valor = plegarMayus((m[1] ?? '').trim()).replace(/[.\s]+$/, '');
        if (ESTADOS_SOCIEDAD.has(valor)) {
            r.estado = valor === 'FUSION' ? 'FUSIÓN' : valor;
            return;
        }
    }
    // Inference from the vigente asientos. Negative guards mirror tipoGravamen's:
    // a lifted suspension / reactivated dissolution leaves the original act in the
    // active registry, so its mere presence is not conclusive. FUSIÓN is never
    // inferred — the surviving company of a merger keeps a (FUSIÓN) act while
    // staying VIGENTE (corpus check: every FUSIÓN inference was a false positive);
    // the act itself remains visible in asientosVigentes.
    const textos = r.asientosVigentes.map((a) => plegarMayus(`${a.acto ?? ''} ${a.descripcion}`));
    const reactivada = textos.some((t) => t.includes('REACTIVACION') ||
        t.includes('LEVANTAMIENTO DE LA SUSPENSION') ||
        t.includes('LEVANTAMIENTO DE SUSPENSION'));
    let inferido;
    if (reactivada)
        inferido = undefined;
    else if (textos.some((t) => t.includes('ANOTACION DE SUSPENSION')))
        inferido = 'SUSPENDIDO';
    else if (textos.some((t) => t.includes('DISOLUCION')))
        inferido = 'DISUELTO';
    if (inferido !== undefined) {
        r.estado = inferido;
        r.estadoInferido = true;
    }
}
/** Canonical charge type for an acto/trámite/description, or undefined. */
function tipoGravamen(textoActo) {
    const t = plegarMayus(textoActo);
    if (t.includes('HIPOTECA') && !t.includes('CANCELACION DE HIPOTECA') && !t.includes('LEVANTAMIENTO')) {
        return 'HIPOTECA';
    }
    if (t.includes('EMBARGO'))
        return 'EMBARGO';
    if (t.includes('SECUESTRO'))
        return 'SECUESTRO';
    if (t.includes('ANOTACION DE ADVERTENCIA'))
        return 'ANOTACION_ADVERTENCIA';
    if (t.includes('ANOTACION DE SUSPENSION'))
        return 'ANOTACION_SUSPENSION';
    if (t.includes('DEMANDA'))
        return 'DEMANDA';
    return undefined;
}
function derivarGravamenes(r) {
    for (const asiento of r.asientosVigentes) {
        const tipo = tipoGravamen(`${asiento.acto ?? ''} ${asiento.descripcion}`);
        if (tipo === undefined)
            continue;
        const gravamen = { tipo, detalle: asiento.descripcion, vigente: true };
        if (asiento.fecha !== undefined)
            gravamen.fecha = asiento.fecha;
        r.gravamenes.push(gravamen);
    }
    // Prelación is historical filing state, not a live charge → vigente: false.
    for (const entrada of r.prelacion) {
        if (entrada.tramite === undefined)
            continue;
        const tipo = tipoGravamen(entrada.tramite);
        if (tipo === undefined)
            continue;
        r.gravamenes.push({
            tipo,
            detalle: `Trámite en prelación (entrada ${entrada.entrada}): ${entrada.tramite}`,
            vigente: false,
        });
    }
}
function derivarAlertas(r) {
    if (r.estado === 'SUSPENDIDO' || r.estado === 'DISUELTO' || r.estado === 'FUSIÓN') {
        const origen = r.estadoInferido === true ? ', inferido de los asientos vigentes' : '';
        r.alertas.push(`La sociedad no está vigente (estado: ${r.estado}${origen}). Aplique diligencia reforzada y verifique el estatus ante el Registro Público.`);
    }
    for (const g of r.gravamenes) {
        if (!g.vigente)
            continue;
        switch (g.tipo) {
            case 'EMBARGO':
                r.alertas.push(`Embargo vigente sobre el bien: ${g.detalle}`);
                break;
            case 'SECUESTRO':
                r.alertas.push(`Secuestro vigente sobre el bien: ${g.detalle}`);
                break;
            case 'HIPOTECA':
                r.alertas.push(`Hipoteca vigente sobre el bien (informativo): ${g.detalle}`);
                break;
            case 'ANOTACION_ADVERTENCIA':
                r.alertas.push(`Anotación de advertencia vigente: ${g.detalle}`);
                break;
            case 'ANOTACION_SUSPENSION':
                r.alertas.push(`Anotación de suspensión vigente: ${g.detalle}`);
                break;
            case 'DEMANDA':
                r.alertas.push(`Demanda vigente registrada sobre el bien: ${g.detalle}`);
                break;
        }
    }
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function parseRpDocumento(texto) {
    const resultado = {
        formato: 'texto_libre',
        miembros: [],
        prelacion: [],
        asientosVigentes: [],
        asientosNoVigentes: [],
        partes: [],
        transacciones: [],
        gravamenes: [],
        referencias: [],
        identificaciones: { cedulas: [], rucs: [] },
        alertas: [],
        advertencias: [],
    };
    let cuerpo = typeof texto === 'string' ? texto : '';
    if (cuerpo.length > MAX_CARACTERES) {
        cuerpo = cuerpo.slice(0, MAX_CARACTERES);
        resultado.advertencias.push(`El texto supera los ${MAX_CARACTERES} caracteres; se analizó únicamente el inicio.`);
    }
    if (cuerpo.trim().length === 0) {
        resultado.advertencias.push('El texto está vacío; no hay nada que analizar.');
        return resultado;
    }
    const buscable = plegarAcentos(cuerpo).toLowerCase();
    if (buscable.includes('[tab:')) {
        resultado.formato = 'extra_datos';
        parseExtraDatos(cuerpo, resultado);
    }
    else if (buscable.includes('codigo de ubicacion') && buscable.includes('folio real')) {
        resultado.formato = 'datos_generales';
        parseDatosGenerales(cuerpo, buscable, resultado);
    }
    // Generic extractors — every format.
    resultado.partes = extraerPartes(cuerpo);
    resultado.transacciones = extraerTransacciones(cuerpo);
    resultado.referencias = unicos([...cuerpo.matchAll(REFERENCIA_FOLIO_RE_G)].map((m) => (m[1] ?? '').trim())).sort();
    resultado.identificaciones.cedulas = unicos([...cuerpo.matchAll(CEDULA_RE_G)].map((m) => (m[1] ?? '').trim()));
    resultado.identificaciones.rucs = unicos([...cuerpo.matchAll(RUC_RE_G)].map((m) => (m[1] ?? '').trim()));
    if (resultado.denominacion !== undefined &&
        !resultado.partes.some((p) => p.nombre === resultado.denominacion)) {
        resultado.partes.push({ rol: 'OWNER', nombre: resultado.denominacion });
    }
    detectarEstado(cuerpo, resultado);
    derivarGravamenes(resultado);
    derivarAlertas(resultado);
    const hayPropietario = resultado.partes.length > 0 ||
        resultado.asientosVigentes.some((a) => a.favorDe !== undefined) ||
        resultado.asientosNoVigentes.some((a) => a.favorDe !== undefined);
    if (resultado.denominacion === undefined &&
        !hayPropietario &&
        resultado.miembros.length === 0 &&
        resultado.folio === undefined) {
        resultado.advertencias.push('No se encontró denominación, propietario, miembros ni folio en el texto; verifique que el texto copiado esté completo.');
    }
    return resultado;
}
//# sourceMappingURL=rp-documento.js.map